export type TriviaQuestionDefinition = {
  id: string;
  question: string;
  definition: string;
};

export type TriviaQuestionPack = {
  id: string;
  label: string;
  questions: TriviaQuestionDefinition[];
};

export const TRIVIA_QUESTION_PACKS: TriviaQuestionPack[] = [
  {
    id: 'core',
    label: 'Core',
    questions: [
      {
        id: 'usd_legal_tender',
        question: 'Which countries use the US dollar as their official legal tender?',
        definition:
          'The US dollar is an official legal tender, either sole or alongside another currency (not merely widely used).',
      },
      {
        id: 'brics_original',
        question: 'Which countries are original members of BRICS?',
        definition:
          'Countries that were part of BRICS before any expansions (Brazil, Russia, India, China, South Africa).',
      },
      {
        id: 'brics_current',
        question: 'Which countries are current members of BRICS?',
        definition: 'Countries that are official BRICS members as of the current reference year.',
      },
      {
        id: 'nato_current',
        question: 'Which countries are current members of NATO?',
        definition: 'Countries that are formal NATO members at the reference date.',
      },
      {
        id: 'un_members',
        question: 'Which countries are members of the United Nations?',
        definition: 'UN member states only (193), excluding observers.',
      },
      {
        id: 'un_observer_states',
        question: 'Which countries are United Nations observer states?',
        definition: 'States with UN observer status, not full membership.',
      },
      {
        id: 'commonwealth_realms',
        question: 'Which countries are Commonwealth realms?',
        definition: 'Countries where the British monarch is the current head of state.',
      },
      {
        id: 'former_commonwealth_realms',
        question: 'Which countries were Commonwealth realms but are no longer?',
        definition:
          'Countries that previously had the British monarch as head of state but later became republics or otherwise removed the monarch.',
      },
      {
        id: 'ymca_countries',
        question: 'Which countries have a YMCA?',
        definition: 'Countries with a YMCA presence listed in the International YMCA directory.',
      },
      {
        id: 'monarchies',
        question: 'Which countries are monarchies?',
        definition:
          'Sovereign states where the head of state is a monarch (constitutional, absolute, or elective monarchies).',
      },
      {
        id: 'oil_producers',
        question: 'Which countries are oil-producing nations?',
        definition:
          'Countries that produce crude oil in commercial quantities (per the referenced list of oil-producing nations).',
      },
      {
        id: 'active_volcanoes',
        question: 'Which countries have active volcanoes?',
        definition:
          'Countries with at least one active volcano on their territory (per the referenced list of active volcanoes).',
      },
      {
        id: 'religious_head_of_state',
        question: 'Which countries have a religious leader as head of state?',
        definition: 'The head of state is a religious figure (not merely religious influence or requirement).',
      },
      {
        id: 'same_sex_marriage_legal',
        question: 'Which countries have legalised same-sex marriage nationwide?',
        definition: 'Same-sex marriage is legal nationwide, by legislation or binding court ruling.',
      },
      {
        id: 'same_sex_marriage_never',
        question: 'Which countries have never legalised same-sex marriage nationwide?',
        definition: 'Same-sex marriage has never been legal nationwide.',
      },
      {
        id: 'elected_female_head_of_state',
        question: 'Which countries have had an elected female head of state?',
        definition:
          'A woman served as head of state, was elected, and held the role in her own right (not appointed or monarch).',
      },
      {
        id: 'never_elected_female_head_of_state',
        question: 'Which countries have never had an elected female head of state?',
        definition: 'No woman has ever served as an elected head of state.',
      },
      {
        id: 'president_head_of_state',
        question: 'Which countries have a president as head of state?',
        definition: 'The constitutional head of state holds the title President.',
      },
      {
        id: 'king_head_of_state',
        question: 'Which countries have a king as head of state?',
        definition: 'The constitutional head of state is a king (not queen regnant, president, or regent).',
      },
      {
        id: 'nuclear_weapons_tested',
        question: 'Which countries have detonated nuclear weapons?',
        definition: 'Countries that have conducted at least one nuclear test explosion, anywhere.',
      },
      {
        id: 'nuclear_detonations_on_territory',
        question: 'Which countries have had nuclear weapons detonated on their territory?',
        definition:
          'At least one nuclear detonation occurred within the country’s territory, airspace, or territorial waters, regardless of who conducted it.',
      },
      {
        id: 'nuclear_weapons_possess',
        question: 'Which countries possess nuclear weapons?',
        definition:
          'Countries believed to currently possess nuclear weapons, whether officially declared or widely accepted de facto.',
      },
      {
        id: 'nuclear_power_plants',
        question: 'Which countries operate nuclear power plants?',
        definition: 'Countries that have operational nuclear reactors generating electricity.',
      },
      {
        id: 'cfa_franc',
        question: 'Which countries use the CFA franc?',
        definition:
          'Countries using the West African (XOF) or Central African (XAF) CFA franc as legal tender.',
      },
      {
        id: 'west_african_cfa',
        question: 'Which countries are in the West African CFA franc zone?',
        definition: 'Countries using XOF (West African CFA franc).',
      },
      {
        id: 'central_african_cfa',
        question: 'Which countries are in the Central African CFA franc zone?',
        definition: 'Countries using XAF (Central African CFA franc).',
      },
      {
        id: 'landlocked',
        question: 'Which countries are landlocked?',
        definition: 'Countries with no coastline on any ocean or sea.',
      },
      {
        id: 'eu_members',
        question: 'Which countries are members of the European Union?',
        definition: 'Countries that are members of the European Union.',
      },
      {
        id: 'former_ussr',
        question: 'Which countries were formerly part of the Soviet Union (USSR)?',
        definition: 'Modern countries that were Soviet republics in the USSR.',
      },
      {
        id: 'national_bird_eagle',
        question: 'Which countries have an eagle as their national bird?',
        definition: 'Countries whose national bird is an eagle.',
      },
      {
        id: 'drives_left',
        question: 'Which countries drive on the left side of the road?',
        definition: 'Countries where road traffic keeps to the left.',
      },
      {
        id: 'landlocked_subset',
        question: 'Which of these countries are landlocked?',
        definition: 'Countries with no coastline on any ocean or sea.',
      },
      {
        id: 'no_rivers',
        question: 'Which countries have no rivers?',
        definition: 'Countries that have no rivers (per the referenced list of countries without rivers).',
      },
      {
        id: 'doubly_landlocked',
        question: 'Which countries are doubly landlocked?',
        definition: 'Countries that are landlocked and are only bordered by other landlocked countries.',
      },
      {
        id: 'one_land_border',
        question: 'Which countries have only one land border?',
        definition: 'Countries that share a land border with exactly one other country.',
      },
      {
        id: 'five_or_more_land_borders',
        question: 'Which countries have five or more land borders?',
        definition: 'Countries bordering five or more distinct sovereign states.',
      },
      {
        id: 'contains_enclaves',
        question: 'Which countries contain enclaves of other countries?',
        definition: 'Countries that fully surround at least one foreign sovereign enclave.',
      },
      {
        id: 'enclave_within_country',
        question: 'Which countries are enclaves within another country?',
        definition: 'Countries that are entirely surrounded by a single other country.',
      },
      {
        id: 'new_7_wonders_2007',
        question: 'Which countries host one of the New 7 Wonders of the World (2007 list)?',
        definition: 'Countries that host one of the New 7 Wonders of the World (2007 list).',
      },
      {
        id: 'new_7_wonders_nature',
        question: 'Which countries contain one of the New 7 Wonders of Nature?',
        definition: 'Countries that contain one of the New 7 Wonders of Nature.',
      },
      {
        id: 'ancient_wonders_modern_countries',
        question:
          'Which modern countries are associated with the Ancient Wonders of the World (based on current borders)?',
        definition: 'Modern countries that contain the sites of the Ancient Wonders of the World.',
      },
      {
        id: 'wonders_near_coast',
        question: 'Which countries host at least one world wonder that is located on or very near the coast?',
        definition: 'Countries that host a world wonder located on or near the coast.',
      },
      {
        id: 'asian_countries_with_wonders',
        question: 'Which Asian countries host at least one world wonder from any major category?',
        definition: 'Asian countries that host at least one world wonder from any major category.',
      },
      {
        id: 'has_exclave',
        question: 'Which countries have exclaves separated from their main territory?',
        definition: 'Countries with territory geographically separated from the main landmass by another country.',
      },
      {
        id: 'named_after_saint',
        question: 'Which countries are named after a saint (in any language)?',
        definition: 'Country name derives from a saint’s name, including translations (Saint, São, San, Santa, etc.).',
      },
      {
        id: 'name_contains_and',
        question: 'Which countries have the word “and” in their English name?',
        definition: 'The official English short name contains the word “and”.',
      },
      {
        id: 'name_contains_z_not_start',
        question: 'Which countries contain the letter Z but do not start with Z?',
        definition: 'Country names containing the letter Z (case-insensitive) that do not begin with Z.',
      },
      {
        id: 'name_ends_with_stan',
        question: 'Which countries end in “-stan”?',
        definition: 'Country names whose English short name ends with “stan”.',
      },
      {
        id: 'name_contains_land',
        question: 'Which countries contain the word “land” in their English name?',
        definition: 'Country names containing the substring “land” (case-insensitive).',
      },
      {
        id: 'named_after_person',
        question: 'Which countries are named after a person?',
        definition: 'Country name derives from a real historical person (religious, royal, or political).',
      },
      {
        id: 'named_after_river',
        question: 'Which countries are named after a river that flows within them?',
        definition: 'Country name derives from a river that flows within or defines part of the country.',
      },
      {
        id: 'named_after_mountain',
        question: 'Which countries are named after a mountain or mountain range?',
        definition: 'Country name explicitly derives from a mountain or mountain range.',
      },
      {
        id: 'name_multiple_words',
        question: 'Which countries’ English names contain more than one word?',
        definition: 'The official English short name contains two or more words.',
      },
      {
        id: 'name_three_or_more_words',
        question: 'Which countries’ English names contain three or more words?',
        definition: 'The official English short name contains three or more words.',
      },
      {
        id: 'flag_horizontal_symmetry',
        question: 'Which countries have a national flag with horizontal symmetry?',
        definition: 'Countries whose national flag is horizontally symmetric.',
      },
      {
        id: 'no_native_snakes',
        question: 'Which countries are naturally free of all types of snakes?',
        definition: 'Countries with no native snake populations.',
      },
      {
        id: 'flag_vertical_symmetry',
        question: 'Which countries have a national flag with vertical symmetry?',
        definition: 'Countries whose national flag is vertically symmetric.',
      },
      {
        id: 'no_native_bears',
        question: 'Which countries are known to have no native populations of bears?',
        definition: 'Countries with no native bear species.',
      },
      {
        id: 'flag_rotational_symmetry',
        question: 'Which countries have a national flag with rotational (180 degree) symmetry?',
        definition: 'Countries whose national flag has 180-degree rotational symmetry.',
      },
      {
        id: 'no_native_scorpions',
        question: 'Which countries are naturally free of scorpions?',
        definition: 'Countries with no native scorpion populations.',
      },
      {
        id: 'flag_horizontal_stripes_only',
        question: 'Which countries have a flag consisting only of horizontal stripes with no symbols?',
        definition: 'Countries whose national flag is made only of horizontal stripes and has no symbols.',
      },
      {
        id: 'flag_nordic_cross',
        question: 'Which countries have a “Nordic Cross” style flag?',
        definition: 'Countries whose national flag uses the Nordic Cross design.',
      },
      {
        id: 'nobel_peace_laureate',
        question: 'Which countries have had a Nobel Peace Prize laureate (citizen at time of award)?',
        definition: 'At least one laureate was a citizen of the country at the time they won.',
      },
      {
        id: 'never_nobel_peace_laureate',
        question: 'Which countries have never had a Nobel Peace Prize laureate?',
        definition: 'No citizen of the country has ever won the Nobel Peace Prize at time of award.',
      },
      {
        id: 'area_over_1m_km2',
        question: 'Which countries are larger than 1 million square kilometres?',
        definition: 'Total land area exceeds 1,000,000 km².',
      },
      {
        id: 'area_under_1000_km2',
        question: 'Which countries are smaller than 1,000 square kilometres?',
        definition: 'Total land area below 1,000 km².',
      },
      {
        id: 'nth_largest_by_area',
        question: 'Which country is the Nth largest by land area?',
        definition: 'Countries ranked by total land area, parameterised by rank N.',
      },
      {
        id: 'borders_pacific',
        question: 'Which countries border the Pacific Ocean?',
        definition: 'Country has a coastline on the Pacific Ocean or its marginal seas.',
      },
      {
        id: 'borders_atlantic',
        question: 'Which countries border the Atlantic Ocean?',
        definition: 'Country has a coastline on the Atlantic Ocean or its marginal seas.',
      },
      {
        id: 'borders_indian',
        question: 'Which countries border the Indian Ocean?',
        definition: 'Country has a coastline on the Indian Ocean or its marginal seas.',
      },
      {
        id: 'borders_southern',
        question: 'Which countries border the Southern Ocean?',
        definition: 'Country has a coastline on the Southern Ocean (as defined by modern geographic convention).',
      },
      {
        id: 'borders_arctic',
        question: 'Which countries border the Arctic Ocean?',
        definition: 'Country has a coastline on the Arctic Ocean.',
      },
      {
        id: 'capital_contains_country_name',
        question:
          'Which countries have a capital city name that contains the country name (case-insensitive; ignore spaces, punctuation, and diacritics)?',
        definition:
          'The normalized capital city name includes the normalized country name as a substring.',
      },
      {
        id: 'capital_contains_port',
        question:
          "Which countries have a capital city name that contains 'Port' (including 'Porto')? (case-insensitive; ignore punctuation)",
        definition:
          'The capital city name includes the token "Port" or "Porto" after removing punctuation.',
      },
      {
        id: 'capital_contains_saint_variant',
        question:
          "Which countries have a capital city name that contains a 'Saint' variant (any of: Saint, St, San, São)? (case-insensitive; ignore punctuation and diacritics)",
        definition:
          'The capital city name includes a Saint variant token after normalization.',
      },
      {
        id: 'capital_contains_city_variant',
        question:
          "Which countries have a capital city name that contains a 'City' variant (any of: City, Ciudad)? (case-insensitive; ignore punctuation)",
        definition:
          'The capital city name includes City or Ciudad after removing punctuation.',
      },
      {
        id: 'capital_shorter_than_country_name',
        question:
          'Which countries have a capital city name that is shorter than the country name, after removing spaces, punctuation, and diacritics?',
        definition:
          'The normalized capital city name is shorter than the normalized country name.',
      },
      {
        id: 'constitutional_monarchies_europe',
        question: 'Which of the following countries is currently a constitutional monarchy in Europe?',
        definition: 'European states with constitutional monarchs as head of state.',
      },
      {
        id: 'elective_monarchies',
        question:
          'Which countries have a monarch elected or selected as head of state instead of being purely hereditary (elective or special monarchy)?',
        definition: 'Monarchies where the head of state is chosen by election or special selection.',
      },
      {
        id: 'commonwealth_realms_subset',
        question:
          'Which countries have a monarch as head of state and are Commonwealth realms (sharing the same monarch)?',
        definition: 'Commonwealth realms sharing the same monarch as head of state.',
      },
      {
        id: 'absolute_monarchies',
        question: 'Which of these countries are absolute monarchies, where the monarch has significant governing power?',
        definition: 'Monarchies where the monarch retains substantial governing authority.',
      },
      {
        id: 'co_monarchs',
        question:
          'Which countries have more than one individual recognized as part of the monarchic head of state (like co-monarchs)?',
        definition: 'Monarchies with shared or co-monarch head of state roles.',
      },
    ],
  },
];

export const TRIVIA_QUESTIONS: TriviaQuestionDefinition[] = TRIVIA_QUESTION_PACKS.flatMap(
  (pack) => pack.questions
);
